import { lazy } from 'react';
import PageLayout from '../layouts/PageLayout';
import BarPreferenceList from '../features/bar-preference/BarPreferenceList';
import BarPreferenceCreateEdit from '../features/bar-preference/BarPreferenceCreateEdit';
import TrendList from '../features/trend/TrendList';
import TrendCreateEdit from '../features/trend/TrendCreateEdit';
import CategoryList from '../features/category/CategoryList';
import CategoryCreateEdit from '../features/category/CategoryCreateEdit';
import CuisineList from '../features/cuisine/CuisineList';
import CuisineCreateEdit from '../features/cuisine/CuisineCreateEdit';
import DietaryPreferenceList from '../features/dietary-preference/DietaryPreferenceList';
import DietaryPreferenceCreateEdit from '../features/dietary-preference/DietaryPreferenceCreateEdit';
import OccasionList from '../features/occasion/OccasionList';
import OccasionCreateEdit from '../features/occasion/OccasionCreateEdit';
import UserList from '../features/user/UserList';
import UserCreateEdit from '../features/user/UserCreateEdit';
import ItemList from '../features/item/ItemList';
import ItemCreateEdit from '../features/item/ItemCreateEdit';
import PostList from '../features/post/PostList';
import PostCreateEdit from '../features/post/PostCreateEdit';
import ActivityList from '../features/activity/ActivityList';
import ActivityCreateEdit from '../features/activity/ActivityCreateEdit';
import KeywordCategoryList from '../features/keyword-category/KeywordCategoryList';
import KeywordCategoryCreateEdit from '../features/keyword-category/KeywordCategoryCreateEdit';
import BusinessKeywordsCategoriesList from '../features/business-keywords-categories/BusinessKeywordsCategoriesList';
import BusinessKeywordsCategoriesCreateEdit from '../features/business-keywords-categories/BusinessKeywordsCategoriesCreateEdit';

// Lazy loaded components
const LoginPage = lazy(() => import("../features/auth/LoginPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const BusinessList = lazy(() => import("../features/business/BusinessList"));
const BusinessCreateEdit = lazy(() => import("../features/business/BusinessCreateEdit"));
const BusinessImport = lazy(() => import("../features/business/BusinessImport"));
const GeoImport = lazy(() => import("../features/geo/GeoImport"));
const GeoBrowseListPage = lazy(() => import("../features/geo/GeoBrowseListPage"));
const VenueLocationList = lazy(() => import("../features/venue-location/VenueLocationList"));
const VenueLocationDetail = lazy(() => import("../features/venue-location/VenueLocationDetail"));
const BookingList = lazy(() => import("../features/booking/BookingList"));
const BookingCreateEdit = lazy(() => import("../features/booking/BookingCreateEdit"));
const AmenityList = lazy(() => import("../features/amenity/AmenityList"));
const AmenityCreateEdit = lazy(() => import("../features/amenity/AmenityCreateEdit"));
const ThreadList = lazy(() => import("../features/thread/ThreadList"));
const ThreadCreateEdit = lazy(() => import("../features/thread/ThreadCreateEdit"));
const DashboardPage = lazy(() => import("../pages/DashboardPage"));

export const routeConfig = {
  public: {
    login: {
      path: '/login',
      component: LoginPage,
    },
    notFound: {
      path: '*',
      component: NotFoundPage,
    },
  },
  private: {
    dashboardHome: {
      path: '/',
      component: DashboardPage,
    },
    business: {
      path: '/business',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: BusinessList,
        },
        create: {
          path: 'create',
          component: BusinessCreateEdit,
        },
        edit: {
          path: 'edit/:businessId',
          component: BusinessCreateEdit,
        },
        import: {
          path: 'import',
          component: BusinessImport,
        },
        geoImport: {
          path: 'geo-import',
          component: GeoImport,
        },
      },
    },
    booking: {
      path: '/booking',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: BookingList,
        },
        create: {
          path: 'create',
          component: BookingCreateEdit,
        },
        edit: {
          path: 'edit/:bookingId',
          component: BookingCreateEdit,
        },
      },
    },
    amenity: {
      path: '/amenity',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: AmenityList,
        },
        create: {
          path: 'create',
          component: AmenityCreateEdit,
        },
        edit: {
          path: 'edit/:amenityId',
          component: AmenityCreateEdit,
        },
      },
    },
    barPreference: {
      path: '/bar-preference',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: BarPreferenceList,
        },
        create: {
          path: 'create',
          component: BarPreferenceCreateEdit,
        },
        edit: {
          path: 'edit/:barPreferenceId',
          component: BarPreferenceCreateEdit,
        },
      },
    },
    trend: {
      path: '/trend',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: TrendList,
        },
        create: {
          path: 'create',
          component: TrendCreateEdit,
        },
        edit: {
          path: 'edit/:trendId',
          component: TrendCreateEdit,
        },
      },
    },
    category: {
      path: '/category',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: CategoryList,
        },
        create: {
          path: 'create',
          component: CategoryCreateEdit,
        },
        edit: {
          path: 'edit/:categoryId',
          component: CategoryCreateEdit,
        },
      },
    },
    cuisine: {
      path: '/cuisine',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: CuisineList,
        },
        create: {
          path: 'create',
          component: CuisineCreateEdit,
        },
        edit: {
          path: 'edit/:cuisineId',
          component: CuisineCreateEdit,
        },
      },
    },
    dietaryPreference: {
      path: '/dietary-preference',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: DietaryPreferenceList,
        },
        create: {
          path: 'create',
          component: DietaryPreferenceCreateEdit,
        },
        edit: {
          path: 'edit/:dietaryPreferenceId',
          component: DietaryPreferenceCreateEdit,
        },
      },
    },
    occasion: {
      path: '/occasion',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: OccasionList,
        },
        create: {
          path: 'create',
          component: OccasionCreateEdit,
        },
        edit: {
          path: 'edit/:occasionId',
          component: OccasionCreateEdit,
        },
      },
    },
    user: {
      path: '/user',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: UserList,
        },
        create: {
          path: 'create',
          component: UserCreateEdit,
        },
        edit: {
          path: 'edit/:userId',
          component: UserCreateEdit,
        },
      },
    },
    item: {
      path: '/item',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: ItemList,
        },
        create: {
          path: 'create',
          component: ItemCreateEdit,
        },
        edit: {
          path: 'edit/:itemId',
          component: ItemCreateEdit,
        },
      },
    },
    post: {
      path: '/post',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: PostList,
        },
        create: {
          path: 'create',
          component: PostCreateEdit,
        },
        edit: {
          path: 'edit/:postId',
          component: PostCreateEdit,
        },
      },
    },
    thread: {
      path: '/thread',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: ThreadList,
        },
        create: {
          path: 'create',
          component: ThreadCreateEdit,
        },
        edit: {
          path: 'edit/:threadId',
          component: ThreadCreateEdit,
        },
      },
    },
    keywordCategory: {
      path: '/keyword-category',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: KeywordCategoryList,
        },
        create: {
          path: 'create',
          component: KeywordCategoryCreateEdit,
        },
        edit: {
          path: 'edit/:keywordCategoryId',
          component: KeywordCategoryCreateEdit,
        },
      },
    },
    businessKeywordsCategories: {
      path: '/business-keywords-categories',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: BusinessKeywordsCategoriesList,
        },
        create: {
          path: 'create',
          component: BusinessKeywordsCategoriesCreateEdit,
        },
        edit: {
          path: 'edit/:id',
          component: BusinessKeywordsCategoriesCreateEdit,
        },
      },
    },
    activity: {
      path: '/activities',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: ActivityList,  
        },
        create: {
          path: 'create',
          component: ActivityCreateEdit,
        },
        edit: {
          path: 'edit/:activityId',
          component: ActivityCreateEdit,  
        },
      },
    },
    geoAdmin: {
      path: '/admin/geo',
      component: PageLayout,
      children: {
        import: {
          path: 'import',
          component: GeoImport,
        },
        countries: {
          path: 'countries',
          component: GeoBrowseListPage,
        },
        regions: {
          path: 'regions',
          component: GeoBrowseListPage,
        },
        cityRegions: {
          path: 'city-regions',
          component: GeoBrowseListPage,
        },
        cities: {
          path: 'cities',
          component: GeoBrowseListPage,
        },
        areas: {
          path: 'areas',
          component: GeoBrowseListPage,
        },
        neighbourhoods: {
          path: 'neighbourhoods',
          component: GeoBrowseListPage,
        },
      },
    },
    venueLocations: {
      path: '/admin/locations',
      component: PageLayout,
      children: {
        list: {
          path: '',
          component: VenueLocationList,
        },
        detail: {
          path: ':locationId',
          component: VenueLocationDetail,
        },
      },
    },
  },
}; 